import { QueryClient } from '@tanstack/react-query';
import type { CourseInfoTabPreviewRequest } from './course-info-tabs-types';
import type { CancellationReasonsPreflightRequest } from './cancellation-reasons-types';

/**
 * Query keys, in one place so an invalidation cannot miss a cache by spelling
 * its key differently.
 *
 * Everything network-scoped is keyed by the company the request was made under,
 * because `X-ZOOZA-COMPANY` is what api-v1 resolves the network from — a cached
 * list must not survive a company switch. The exceptions are the
 * `network/{id}/…` lists, which name the network in the path: those are keyed
 * by that same `networks.id` (FRA-20261025-001, lib/reference-queries.ts;
 * course info tabs, FRA-20261025-002, lib/course-info-tabs-queries.ts;
 * cancellation reasons, FRA-20261028-001, lib/cancellation-reasons-queries.ts).
 * The network comes from the company, so a switch to another network's
 * company changes the key and re-reads instead of serving the old list.
 */
export const qk = {
    referenceData: {
        productCodes: (networkId: number) => ['reference', 'product-codes', networkId] as const,
        places: () => ['reference', 'places'] as const,
        courses: (networkId: number) => ['reference', 'courses', networkId] as const,
    },
    customReports: {
        schema: () => ['custom-reports', 'schema'] as const,
        definitions: () => ['custom-reports', 'definitions'] as const,
        definition: (id: number) => ['custom-reports', 'definition', id] as const,
        runs: (definitionId: number, period: string) =>
            ['custom-reports', 'runs', definitionId, period] as const,
        results: (definitionId: number, period: string) =>
            ['custom-reports', 'results', definitionId, period] as const,
        externalRows: (definitionId: number, period: string) =>
            ['custom-reports', 'external-rows', definitionId, period] as const,
    },
    views: {
        list: (viewType: string) => ['views', viewType] as const,
        default: (viewType: string) => ['views', 'default', viewType] as const,
    },
    // Keyed by `networks.id` — the routes name the network in the path
    // (FRA-20261025-002). Every key starts with `all(networkId)`, so one
    // prefix invalidation reaches the list, each tab, its exceptions and any
    // cached preview count after a write.
    courseInfoTabs: {
        all: (networkId: number) => ['course-info-tabs', networkId] as const,
        list: (networkId: number) => ['course-info-tabs', networkId, 'list'] as const,
        tab: (networkId: number, id: number) => ['course-info-tabs', networkId, 'tab', id] as const,
        exceptions: (networkId: number, tabId: number) =>
            ['course-info-tabs', networkId, 'exceptions', tabId] as const,
        preview: (networkId: number, request: CourseInfoTabPreviewRequest) =>
            ['course-info-tabs', networkId, 'preview', request] as const,
    },
    // Keyed by `networks.id` like course info tabs (FRA-20261028-001). The
    // single `GET` is the whole screen, so `list` is the one query every write
    // invalidates through `all(networkId)` — along with any cached preflight.
    cancellationReasons: {
        all: (networkId: number) => ['cancellation-reasons', networkId] as const,
        list: (networkId: number) => ['cancellation-reasons', networkId, 'list'] as const,
        preflight: (networkId: number, request: CancellationReasonsPreflightRequest) =>
            ['cancellation-reasons', networkId, 'preflight', request] as const,
    },
} as const;

/**
 * Defaults chosen for what this app actually serves.
 *
 * Reference data (places, product codes, course names) is effectively static
 * for a session — it used to be held in module-level promise caches that never
 * expired and could not be invalidated. Report data is a nightly read model, so
 * it does not need aggressive refetching either; what it DOES need is to not be
 * refetched on every window focus while someone is typing figures into an
 * external row.
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // The read model behind these screens is baked nightly; a
                // minute of staleness is invisible and saves a request per
                // remount.
                staleTime: 60_000,
                gcTime: 5 * 60_000,
                // The custom-report surface has inline editing. Refetching
                // because the user alt-tabbed would fight a half-typed row.
                refetchOnWindowFocus: false,
                refetchOnReconnect: true,
                // api-v1 returns 4xx for validation and for a dead token alike;
                // retrying either is pointless noise. A single retry covers a
                // genuinely flaky connection.
                retry: (failureCount, error) => {
                    const status = (error as { response?: { status?: number } })?.response?.status;
                    if (typeof status === 'number' && status >= 400 && status < 500) return false;
                    return failureCount < 1;
                },
            },
            mutations: {
                retry: false,
            },
        },
    });
}
